// @ts-check
import React from 'react';
import { injectIntl } from 'react-intl';
import BusComponent from '../BusComponent';
import AppConfig from '../../appConfig';
import CenterMapLayerButton from './CenterMapLayerButton';
import AreaSelector from './AreaSelector';
import LocationAnalysisReportPopup from './LocationAnalysisReportPopup';
import VisualReportSave from './VisualReportSave';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import { mapLocationAnalysisItemToVisualReport } from '../../helpers/Util';
import {
    buildInsightsUrl,
    normalizeReportFromPath,
    resolveInsightsReport,
} from '../../helpers/insightsUrl';
import SubscriptionProducts from '../../enums/SubscriptionProducts';

const IFRAME_LOAD_TIMEOUT = 15000;
const IFRAME_LOADER_DELAY = 200;
const COVER_NONE = 'none';
const COVER_SURFACE = 'surface';
const OPEN_TAB_FEEDBACK = 700;
const PANEL_READY_GRACE = 2000;

/**
 * @typedef Props
 * @property {import('../../objects/MapInstance').default} mapInstance
 * @property {import('../../objects/User').default} [currentUser]
 * @property {import('react-intl').intlShape} intl
 * @property {boolean} [collapsed]
 * @property {() => void} [onExpand]
 *
 * @typedef State
 * @property {import('../../types').VisualReport} visualReport
 * @property {boolean} showReport
 * @property {string} helpTourReport
 * @property {boolean} showEditPopup
 * @property {string} editMarketUrl
 * @property {'none' | 'surface'} iframeCover
 * @property {boolean} openingTab
 *
 * @extends {BusComponent<Props, State>}
 */
class ReportIframe extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            visualReport: !!this.props.mapInstance.locationAnalysisItem
                ? mapLocationAnalysisItemToVisualReport(this.props.mapInstance.locationAnalysisItem)
                : undefined,
            showReport: !!this.props.mapInstance.locationAnalysisItem,
            helpTourReport: undefined,
            showEditPopup: false,
            editMarketUrl: '',
            iframeCover: COVER_NONE,
            openingTab: false,
        };
        this.renderedSrc = '';
        this.renderedEdit = null;
    }

    componentDidMount() {
        this.bindGluBusEvents({
            LOCATION_ANALYSIS_ITEM_UPDATED: this.onPinLocationUpdated,
            LOCATION_ANALYSIS_MODE_STARTED: this.startUpdateLocationAnalysis,
            LOCATION_ANALYSIS_MODE_EXITED: this.onExitLocationAnalysis,
            SHOW_INSIGHTS: this.onShowInsights,
            INSIGHTS_STATE_REQUEST: this.onInsightsState,
            INSIGHTS_REPORT_UPDATE_REQUEST: this.onInsightsReportUpdate,
            COLLAPSE_ALL: this.onClose,
            CLOSE_MODAL: this.onClosePopup,
            OPEN_EDIT_MARKET_POPUP_REQUEST: this.onOpenEditPopup,
            ACTIVE_DEAL_LIST: this.onActiveDealList,
        });
        window.addEventListener('message', this.handlePostMessage);

        this.renderedSrc = this.state.showEditPopup ? this.state.editMarketUrl : this.getUrl(true);
        if (this.renderedSrc) this.startIframeLoading();
    }

    componentDidUpdate() {
        // Every ring change, pin move or edit-view switch rewrites the iframe
        // src, and the panel is blank until the new document paints. Cover that
        // gap with the panel's own loader.
        const src = this.state.showEditPopup ? this.state.editMarketUrl : this.getUrl(true);
        if (!src || src === this.renderedSrc) return;

        this.renderedSrc = src;
        this.startIframeLoading();
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
        window.removeEventListener('message', this.handlePostMessage);
        this.clearIframeTimers();
        clearTimeout(this.openTabTimer);
    }

    startIframeLoading = () => {
        this.clearIframeTimers();
        if (this.state.iframeCover === COVER_NONE) {
            this.setState({ iframeCover: COVER_SURFACE });
        }
        this.iframeLoadingDelay = setTimeout(() => {
            // A document that never fires `load` must not leave the panel covered.
            this.iframeLoadingTimer = setTimeout(this.uncoverIframe, IFRAME_LOAD_TIMEOUT);
        }, IFRAME_LOADER_DELAY);
    };

    onIframeLoad = () => {
        // `load` only means the document arrived; the native panel has not mounted
        // yet, so it reports for itself and the cover waits for that, bounded in
        // case it never does.
        if (this.state.iframeCover !== COVER_NONE) {
            this.clearIframeTimers();
            this.iframeLoadingTimer = setTimeout(this.uncoverIframe, PANEL_READY_GRACE);
            return;
        }
        this.uncoverIframe();
    };

    uncoverIframe = () => {
        this.clearIframeTimers();
        this.renderedEdit = this.state.showEditPopup;
        if (this.state.iframeCover !== COVER_NONE) this.setState({ iframeCover: COVER_NONE });
    };

    clearIframeTimers = () => {
        clearTimeout(this.iframeLoadingDelay);
        clearTimeout(this.iframeLoadingTimer);
    };

    onActiveDealList = ({ dealList }) => {
        this.activeListId = dealList ? dealList.id : undefined;
    };

    onOpenEditPopup = ({ visualReport, elementToFocus }) => {
        this.emit('ACTIVE_DEAL_LIST_GET_REQUEST');
        this.setState({
            showReport: true,
            showEditPopup: true,
            editMarketUrl: buildInsightsUrl({
                visualReport,
                edit: true,
                elementToFocus,
                listId: this.activeListId,
            }),
        });
    };

    onPinLocationUpdated = ({ mapInstanceId, locationAnalysisItem }) => {
        if (this.props.mapInstance.id !== mapInstanceId) return;

        const nextVR = mapLocationAnalysisItemToVisualReport(locationAnalysisItem);

        this.setState({ visualReport: nextVR });
    };

    onClosePopup = () => {
        this.setState({
            showEditPopup: false,
        });
        this.bus.emit('POPUP_STATE_CHANGED', { isOpen: false });
    };

    /**
     * @param {object} payload
     * @param {string} payload.mapInstanceId
     */
    startUpdateLocationAnalysis = ({ mapInstanceId }) => {
        if (this.props.mapInstance.id !== mapInstanceId) return;

        const visualReport = mapLocationAnalysisItemToVisualReport(
            this.props.mapInstance.locationAnalysisItem,
        );

        this.setState({
            showReport: true,
            visualReport,
        });
    };

    onShowInsights = visualReport => {
        if (this.state.showEditPopup) {
            // A new facility was selected while the edit panel was open.
            // Close the edit popup and navigate to the new facility's report.
            this.setState({
                visualReport,
                showEditPopup: false,
            });
            this.bus.emit('POPUP_STATE_CHANGED', { isOpen: false });
        } else {
            this.setState({ visualReport });
        }
    };

    onExitLocationAnalysis = ({ mapInstanceId }) => {
        if (this.props.mapInstance.id !== mapInstanceId) return;
        this.setState({
            showReport: false,
            visualReport: undefined,
        });
    };

    handlePostMessage = event => {
        if (
            ![
                AppConfig.constants.backends.accounts,
                AppConfig.constants.links.dashboardLink,
            ].includes(event.origin)
        )
            return;
        if (event.data && event.data.url && event.data.action === 'openTab') {
            window.open(event.data.url, '_blank').focus();
        }
        if (event.data && event.data.type === 'PANEL_READY') {
            this.uncoverIframe();
        }
        if (event.data && event.data.type === 'REPORT_LIMITS_POPUP_REQUEST') {
            this.emit('REPORT_LIMITS_POPUP_REQUEST');
        }
        if (event.data && event.data.type === 'REPORT_LIMITS_INCREMENT') {
            this.emit('REPORT_LIMITS_INCREMENT_USAGE_FROM_INFO_PANEL_REQUEST', {
                limits: event.data.payload,
            });
        }
        if (event.data && event.data.type === 'OPEN_EDIT_MARKET_FROM_NOTES') {
            const elementToFocus =
                event.data.payload && event.data.payload.elementToFocus
                    ? event.data.payload.elementToFocus
                    : '';

            this.onOpenEditPopup({
                visualReport: this.state.visualReport,
                elementToFocus: elementToFocus,
            });
        }

        if (event.data && event.data.type === 'INSIGHT_REPORT_UPDATED') {
            const pathname = event.data.payload && event.data.payload.pathname;
            if (typeof pathname === 'string') {
                const report = normalizeReportFromPath(pathname);
                const currentReport = this.state.visualReport && this.state.visualReport.report;
                const currentPath = this.props.mapInstance.locationAnalysisItem
                    ? this.props.mapInstance.locationAnalysisItem.currentInsightReport
                    : undefined;

                // STAR-1485: short-circuit when the iframe is merely re-announcing
                // the report it is already on. Without this guard, each mount of
                // the in-iframe <ReportSwitcher> triggers a setState that creates
                // a new `visualReport` object reference, which forces a re-render
                // where React re-writes the <iframe src=…> attribute. Because
                // `getUrl()` and the iframe's in-flight hash navigation can
                // transiently disagree during a report switch, that src write
                // reloads the iframe, remounts its React tree, re-fires initial
                // fetches, and posts INSIGHT_REPORT_UPDATED again — a feedback
                // loop that saturates the "/api/v1/data/site-analysis/single"
                // endpoint and leaves the "creating insights" loader stuck.
                if (currentReport === report && currentPath === pathname) {
                    return;
                }

                // STAR-1485: mutate locationAnalysisItem.currentInsightReport
                // BEFORE calling setState. This handler runs from a window
                // 'message' listener, not a React synthetic event, so in
                // React 15 setState flushes render() synchronously. getUrl()
                // (invoked during that render) prefers currentInsightReport
                // over state.visualReport.report — if we setState first, the
                // render would read the STALE path and re-write the iframe
                // src to the previous report's URL, kicking off a parent↔
                // iframe ping-pong loop. Mutating first means the synchronous
                // render sees fresh values for both, and the short-circuit
                // above catches the iframe's remount echo.
                if (this.props.mapInstance.locationAnalysisItem) {
                    this.props.mapInstance.locationAnalysisItem.currentInsightReport = pathname;

                    // The dashboard panel carries the report in a query param, so a
                    // re-render here rewrites the iframe src and reloads it on every
                    // switch. The mutation above is all the map needs — getUrl()
                    // prefers currentInsightReport over state.visualReport.report.
                    return;
                }

                this.setState(prev => ({
                    visualReport: { ...prev.visualReport, report },
                }));
            }
        }

        if (event.data && event.data.type === 'VISUAL_REPORT_UPDATED') {
            const updatedVR = event.data.payload;
            this.emit('VISUAL_REPORT_UPDATE_LOCAL_REQUEST', { visualReport: updatedVR });

            // Update all deal fields on the active location analysis item
            this.bus.emit('UPDATE_ACTIVE_LOCATION_ANALYSIS_ITEM_DEAL_DATA', {
                mapInstanceId: this.props.mapInstance.id,
                dealData: updatedVR,
            });
            return;
        }

        if (event.data && event.data.type === 'EDIT_MODE' && event.data.state === 'exit') {
            const { updatedVisualReport, savedDeal, keepEditOpen } = event.data.payload || {};

            if (savedDeal) {
                this.emit('VISUAL_REPORT_SAVE_REQUEST', { visualReport: savedDeal });
                if (savedDeal.id) {
                    this.bus.emit('SET_ACTIVE_LOCATION_ANALYSIS_ITEM_DEAL_ID', {
                        mapInstanceId: this.props.mapInstance.id,
                        dealId: savedDeal.id,
                    });
                }
            }

            if (!keepEditOpen) {
                this.setState({
                    visualReport: updatedVisualReport || this.state.visualReport,
                    showEditPopup: false,
                });
            }
        }
    };

    onInsightsState = () => {
        this.emit('INSIGHTS_STATE', { open: this.state.showReport });
    };

    onInsightsReportUpdate = ({ report }) => {
        this.setState({ helpTourReport: report });
        this.emit('INSIGHTS_REPORT_UPDATED');
    };

    getUrl = frame => {
        const { visualReport, helpTourReport } = this.state;
        if (!visualReport) return '';
        const { locationAnalysisItem } = this.props.mapInstance;

        return buildInsightsUrl({
            visualReport,
            report: resolveInsightsReport({
                visualReport,
                currentInsightReport: locationAnalysisItem
                    ? locationAnalysisItem.currentInsightReport
                    : undefined,
                helpTourReport,
            }),
            frame,
        });
    };

    onClose = () => {
        this.emit('CLOSE_LOCATION_ANALYSIS_REQUEST', {
            mapInstanceId: this.props.mapInstance.id,
        });
    };

    onOpenInNewTab = () => {
        // `getUrl(false)` is the full-screen view, the dashboard's own `/insights`.
        const url = this.getUrl(false);
        if (!url) return;

        this.setState({ openingTab: true });
        clearTimeout(this.openTabTimer);
        this.openTabTimer = setTimeout(() => {
            this.setState({ openingTab: false });
        }, OPEN_TAB_FEEDBACK);

        window.open(url, '_blank');
    };

    onEnter = () => {
        this.bus.emit('IFRAME_MOUSE_ENTER');
    };

    render() {
        const { currentUser, mapInstance, intl, collapsed, onExpand } = this.props;

        const { showReport, showEditPopup, visualReport, editMarketUrl, iframeCover, openingTab } =
            this.state;

        if (!showReport && !showEditPopup) {
            return null;
        }
        const url = this.getUrl(true);

        const hasInternalToolsEnabled =
            (currentUser &&
                currentUser.hasFeatureToggle(
                    SubscriptionProducts.ApplicationAccessConfigurationOptions
                        .ENABLE_INTERNAL_TOOLS,
                )) ||
            false;

        const locationAnalysisItem = mapInstance.locationAnalysisItem;

        const iframeSrc = showEditPopup ? editMarketUrl : url;
        const isNavigating = Boolean(iframeSrc) && iframeSrc !== this.renderedSrc;
        const showCover = isNavigating || iframeCover !== COVER_NONE;
        const editChrome =
            showCover && this.renderedEdit !== null ? this.renderedEdit : showEditPopup;

        const header = (
            <div
                className="report-iframe__iframe-header"
                style={{ borderBottom: editChrome ? 'none' : undefined }}
            >
                <AreaSelector
                    mapInstanceId={mapInstance.id}
                    locationAnalysisItem={locationAnalysisItem}
                    allowCustomShape
                />

                <div className="report-iframe__iframe-header--actions">
                    <VisualReportSave visualReport={visualReport} />
                    <LocationAnalysisReportPopup mapInstanceId={mapInstance.id} />
                    <CenterMapLayerButton
                        isVisible={hasInternalToolsEnabled}
                        mapInstanceId={mapInstance.id}
                        zoomPoint={visualReport.point}
                    />

                    <div className="divider divider--vertical-small" />

                    <button
                        onClick={this.onClose}
                        className="material-icons btn-flat-icon"
                        title="Close analysis panel"
                    >
                        close
                    </button>
                </div>
            </div>
        );

        return (
            <div
                className={`report-iframe-wrapper ${collapsed ? 'report-iframe-wrapper--collapsed' : ''}`}
                data-tourId={HelpTourTargets.INFO_PANEL}
            >
                <div className="report-iframe" onMouseEnter={this.onEnter}>
                    {!editChrome && header}

                    <div
                        className={`report-iframe__iframe ${editChrome ? 'report-iframe__iframe--edit' : ''}`}
                    >
                        <iframe src={iframeSrc} title="Visual report" onLoad={this.onIframeLoad} />
                        {showCover && <div className="report-iframe__cover" />}
                    </div>

                    {!editChrome && (
                        <div className="report-iframe__iframe-footer">
                            <div data-tourId={HelpTourTargets.INFO_PANEL_FULL_SCREEN}>
                                <button
                                    onClick={this.onOpenInNewTab}
                                    disabled={openingTab}
                                    className={`btn-raised btn-rounded report-iframe__analyze ${
                                        openingTab ? 'report-iframe__analyze--opening' : ''
                                    }`}
                                >
                                    {intl.formatMessage({ id: 'locationAnalysis.analyzeDeal' })}
                                    <i className="material-icons">
                                        {openingTab ? 'open_in_new' : 'arrow_forward'}
                                    </i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {collapsed && (
                    <button
                        className="report-iframe-bar"
                        onClick={onExpand}
                        aria-label={intl.formatMessage({ id: 'map.showInsightPanel' })}
                        data-testid="insight-panel-expand"
                    >
                        <i className="material-symbols">arrow_left</i>
                    </button>
                )}
            </div>
        );
    }
}

export default injectIntl(ReportIframe);
